import React, { Fragment, forwardRef, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import {
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useMediaQuery
} from '@mui/material';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { SlideTransition } from '../Transition/Transition';
import 'react-spring-bottom-sheet/dist/style.css';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { acquireScrollLockRecovery, markSheetScrollLock } from '../../utils/scrollLock';

const BottomSheetOrDialog = forwardRef((props, ref) => {
  const {
    intl,
    open,
    className,
    onClose,
    onExited,
    header,
    headerCTAs,
    actions,
    actionsPrepend,
    content,
    BottomSheetProps,
    BottomSheetParams,
    DialogProps,
    DialogTitleProps,
    DialogContentProps,
    DialogActionsProps,
    DialogParams
  } = props;

  const shouldUseBottomSheet = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const _headerCTAs = (headerCTAs || []).filter(Boolean);
  const _actions = actions || [];
  const _actionsPrepend = actionsPrepend || [];

  // Only the sheet branch can put a SECOND owner on the body's scroll lock, so
  // only that branch arms the recovery sweep. A desktop render below is a MUI
  // Dialog and nothing else — MUI's own ModalManager stacks correctly, and the
  // sweep would have nothing to repair.
  useEffect(() => {
    if (!shouldUseBottomSheet) return undefined;
    return acquireScrollLockRecovery();
  }, [shouldUseBottomSheet]);

  // The opening edge is where this pair claims the lock, because it is where
  // react-spring-bottom-sheet is about to take it. The claim is what lets the
  // sweep repair by ownership instead of assuming every stray `overflow:
  // hidden` is its own; it is cleared by the sweep, not here, since the value
  // it has to undo is written after the sheet has already gone.
  useEffect(() => {
    if (shouldUseBottomSheet && open) markSheetScrollLock();
  }, [shouldUseBottomSheet, open]);

  if (shouldUseBottomSheet) {
    const { disableAutoDismiss, onSpringEnd, BoxProps = {}, ...restBottomSheetProps } = BottomSheetProps || {};
    const { height: boxHeight, sx: boxSx, ...restBoxProps } = BoxProps;
    // `zIndex` lifts the sheet's backdrop for a sheet opened over a MUI Dialog (1300+); the
    // sheet itself is lifted by the caller's className, since rsbs sets its z-index in CSS.
    const { hideCloseButton, headerStyle, zIndex } = BottomSheetParams || {};
    const onDismiss = event => {
      if (onClose) {
        onClose(event);
      }
    };
    const _onSpringEnd = event => {
      const { type } = event || {};
      if (onSpringEnd) {
        onSpringEnd(event);
      }
      if (type === 'CLOSE') {
        if (onExited) {
          setTimeout(() => {
            onExited();
          }, 25);
        }
      }
    };

    return (
      <Fragment>
        <Backdrop open={open} onClick={!disableAutoDismiss ? onClose : undefined} style={{ zIndex: zIndex || 1043 }} />
        <BottomSheet
          className={className}
          open={open}
          onDismiss={!disableAutoDismiss ? onDismiss : undefined}
          onSpringEnd={_onSpringEnd}
          header={
            <Grid
              container
              sx={{
                alignItems: 'flex-start',
                justifyContent: 'space-between'
              }}
            >
              <Grid>
                <Typography color="primary" align="left" component="div" style={{ ...(headerStyle || {}) }}>
                  {header}
                </Typography>
              </Grid>
              <Grid>
                <Grid
                  container
                  spacing={1}
                  sx={{
                    alignItems: 'center'
                  }}
                >
                  {_headerCTAs.map((cta, index) => (cta ? <Grid key={index}>{cta}</Grid> : null))}
                  {!hideCloseButton && (
                    <Grid>
                      <IconButton
                        onClick={onClose}
                        style={{ padding: 1, color: 'var(--rsbs-handle-bg,hsla(0, 0%, 0%, 0.14))' }}
                        size="large"
                        aria-label={intl.formatMessage({ id: 'Close' })}
                      >
                        <ClearRoundedIcon />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          }
          footer={
            (_actionsPrepend.length > 0 || _actions.length > 0) && (
              <Fragment>
                <Grid
                  container
                  style={{ width: '100%' }}
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Grid>
                    <Grid
                      container
                      sx={{
                        display: 'flex'
                      }}
                    >
                      {_actionsPrepend.map((cta, index) =>
                        cta ? (
                          <Grid key={index} style={{ alignSelf: 'center' }}>
                            {cta}
                          </Grid>
                        ) : null
                      )}
                    </Grid>
                  </Grid>
                  <Grid>
                    {_actions.length > 0 && (
                      <Grid
                        container
                        sx={{
                          alignItems: 'center'
                        }}
                      >
                        {(_actions || []).map((cta, index) => (cta ? <Grid key={index}>{cta}</Grid> : null))}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Box style={{ width: '100%', height: 'var(--ion-safe-area-bottom, 0px)' }} />
                <Box
                  style={{
                    width: '100%',
                    height: 'var(--ion-safe-area-bottom, 0px)'
                  }}
                />
              </Fragment>
            )
          }
          blocking={false}
          expandOnContentDrag={true}
          ref={ref}
          {...restBottomSheetProps}
        >
          {/* This Box is the element that scrolls (a caller-fixed height with
              overflow: auto). react-spring-bottom-sheet's iOS body-scroll-lock
              cancels touchmove on anything not opted out, so without the marker
              the content can't be scrolled on iPhone Safari. */}
          <Box
            data-body-scroll-lock-ignore="true"
            {...restBoxProps}
            style={{ overflow: 'auto', height: boxHeight || 'max-content' }}
            sx={[
              {
                padding: 2
              },
              ...(Array.isArray(boxSx) ? boxSx : [boxSx])
            ].filter(Boolean)}
          >
            {content}
            <Box
              style={{
                width: '100%',
                height: 'var(--ion-safe-area-bottom, 0px)'
              }}
            />
          </Box>
        </BottomSheet>
      </Fragment>
    );
  } else {
    const { leftCloseButton, dialogTitleCloseButton, hideCloseButton, headerStyle } = DialogParams || {};
    // MUI v9 Dialog removed PaperProps in favour of slotProps.paper. Legacy
    // callers still pass DialogProps.PaperProps — extract it here and forward
    // through slotProps.paper so existing behaviour is preserved.
    const { PaperProps: legacyPaperProps, slots: callerSlots, ...DialogPropsRest } = DialogProps || {};

    return (
      <Dialog
        className={className}
        open={open}
        onClose={onClose}
        ref={ref}
        {...DialogPropsRest}
        slots={{
          transition: SlideTransition,
          ...callerSlots
        }}
        slotProps={{
          transition: {
            onExited
          },
          ...(legacyPaperProps ? { paper: legacyPaperProps } : {})
        }}
      >
        <DialogTitle {...DialogTitleProps}>
          <Grid
            container
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Grid>
              <Typography color="primary" variant="h6" style={{ ...(headerStyle || {}) }}>
                {header}
              </Typography>
            </Grid>
            {_headerCTAs.length > 0 && (
              <Grid>
                <Grid
                  container
                  sx={{
                    alignItems: 'center'
                  }}
                >
                  {_headerCTAs.map((cta, index) => (cta ? <Grid key={index}>{cta}</Grid> : null))}
                </Grid>
              </Grid>
            )}
            {dialogTitleCloseButton && (
              <Grid>
                <IconButton
                  onClick={onClose}
                  style={{ padding: 1, color: 'var(--rsbs-handle-bg,hsla(0, 0%, 0%, 0.14))' }}
                  size="large"
                >
                  <ClearRoundedIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </DialogTitle>
        <DialogContent {...DialogContentProps}>{content}</DialogContent>
        <DialogActions {...DialogActionsProps}>
          <Grid
            container
            style={{ width: '100%' }}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Grid>
              <Grid
                container
                sx={{
                  alignItems: 'center'
                }}
              >
                {leftCloseButton && !dialogTitleCloseButton && (
                  <Grid>
                    <Button color="primary" onClick={onClose}>
                      {intl.formatMessage({ id: 'Close' })}
                    </Button>
                  </Grid>
                )}
                {_actionsPrepend.map((cta, index) => (cta ? <Grid key={index}>{cta}</Grid> : null))}
              </Grid>
            </Grid>
            <Grid>
              <Grid
                container
                sx={{
                  alignItems: 'center'
                }}
              >
                {!leftCloseButton && !dialogTitleCloseButton && !hideCloseButton && (
                  <Grid>
                    <Button color="primary" onClick={onClose}>
                      {intl.formatMessage({ id: 'Close' })}
                    </Button>
                  </Grid>
                )}
                {_actions.map((cta, index) => (cta ? <Grid key={index}>{cta}</Grid> : null))}
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  }
});

export default injectIntl(BottomSheetOrDialog);
